import { LeftOutlined, PrinterOutlined } from '@ant-design/icons'
import { Button, Card, Flex, Form, FormInstance, Input, Space, Tag } from 'antd'
import { UploadFile } from 'antd/lib'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import {
  useStocksServiceCreateInternalIssuanceEndpoint,
  useStocksServiceRejectInternalInquiryEndpoint
} from 'src/api/queries'
import {
  ApiError,
  CreateInventoryIssuanceItemDto,
  InternalInquiryDocumentResponse,
  InternalInventoryDocumentState
} from 'src/api/requests'
import {
  CustomItemRender,
  message,
  SaveConfirm,
  Text,
  Upload
} from 'src/components'
import { ConfirmModal } from 'src/components/ConfirmModal'
import { getStatus } from 'src/features/stock/utils'
import { useStyles } from 'src/features/stock-administration/components/Applications/styles'
import { LocalAdminConsumableTable } from 'src/features/stock-local/components/applications/issuance'
import {
  FileDocument,
  onDownloadFile,
  useFileHandler,
  useScrollToFirstError,
  ValidationError
} from 'src/hooks'
import {
  dayMonthYearHourMinutesFormatSeparator,
  yearMonthDaySlash
} from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

type Props = {
  data?: InternalInquiryDocumentResponse
  form: FormInstance
}

export const StockLocalIssuanceApplicationsWaiting = ({
  data,
  form
}: Props) => {
  const [isEdit, setIsEdit] = useState(false)
  const [isReject, setIsReject] = useState(false)

  const { onScrollToError } = useScrollToFirstError(form)

  const { styles } = useStyles()
  const palette = useTheme()

  const { t } = useLocales()
  const navigate = useNavigate()

  const { id } = useParams()

  const printableRef = useRef<HTMLDivElement>(null)

  const onPrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `Stock_Application_${id}`
  })

  const { setExistingFiles, onFileChange, saveFiles, isUploading } =
    useFileHandler({
      form,
      folder: id ? `issuance-${id}` : 'inquiries'
    })

  const onBackClick = () => {
    navigate(paths.stockLocal.applications)
  }

  const { mutate: reject } = useStocksServiceRejectInternalInquiryEndpoint({
    onSuccess: () => {
      message.success(t('stock_applications.rejected_success'))
      navigate(paths.stockLocal.applications)
    },
    onError: (e: ApiError) => formErrorHandler(e.body.errors)
  })

  const { mutate, isPending } = useStocksServiceCreateInternalIssuanceEndpoint({
    onSuccess: () => {
      message.success(t('stock_applications.successfully_issued'))
      navigate(paths.stockLocal.applications)
    },
    onError: (e: ApiError) => formErrorHandler(e.body.errors)
  })

  const onSubmit = async () => {
    try {
      const values = await form.validateFields()

      const updatedFiles = await saveFiles()

      mutate({
        requestBody: {
          baseDocumentId: id,
          documentDate: dayjs(data?.documentDate).format(yearMonthDaySlash),
          comment: values?.comment,
          mediaItems: updatedFiles,
          items: values?.medicine?.map(
            (item: CreateInventoryIssuanceItemDto) => ({
              inventoryItemId: item?.inventoryItemId,
              quantity: item?.quantity
            })
          )
        }
      })
    } catch (error) {
      onScrollToError(error as ValidationError)
    }
  }

  const onReject = () => {
    reject({
      id: String(id),
      requestBody: {
        comment: form.getFieldValue('commentReject')
      }
    })
    setIsReject(false)
  }

  useEffect(() => {
    if (data) {
      const existingFileIds = data?.mediaFiles
        ?.map((file) => file?.id)
        .filter(Boolean) as string[]

      setExistingFiles(existingFileIds)

      const initialFormValues = {
        documentDate: data?.documentDate
          ? dayjs(data?.documentDate)
          : undefined,
        docs: existingFileIds.map((id) => ({
          uid: id,
          name: `File-${id}`,
          status: 'done'
        })),
        inquiryItems: data?.items?.map((item) => ({
          name: `${item?.internationalName?.name} ${item?.internationalName?.dosage}`,
          internationalNameId: item?.internationalName?.id,
          form: {
            name: item?.productForm?.name,
            id: item?.productForm?.id
          },
          quantity: item?.quantity,
          items: item?.roleStocks || []
        }))
      }

      form.setFieldsValue(initialFormValues)
    }
  }, [data])

  return (
    <>
      <Flex vertical gap={24}>
        <Flex justify="space-between">
          <Button
            icon={<LeftOutlined />}
            className="fit-content"
            onClick={onBackClick}
          >
            {t('common.back')}
          </Button>

          <Space size={8}>
            <Button icon={<PrinterOutlined />} onClick={() => onPrint()}>
              {t('stock_applications.print')}
            </Button>

            <>
              <SaveConfirm
                title={`${t('stock_administration.reject')}?`}
                placement="leftBottom"
                onConfirm={() => setIsReject(true)}
              >
                <Button danger>{t('stock_administration.reject')}</Button>
              </SaveConfirm>

              <SaveConfirm
                title={`${t(`stock_applications.${isEdit ? 'confirm' : 'release'}`)}`}
                placement="leftBottom"
                onConfirm={() => (isEdit ? onSubmit() : setIsEdit(true))}
                okButtonProps={{ loading: isUploading || isPending }}
              >
                <Button type="primary">
                  {t(`stock_applications.${isEdit ? 'confirm' : 'release'}`)}
                </Button>
              </SaveConfirm>
            </>
          </Space>
        </Flex>

        <Flex vertical gap={16} ref={printableRef}>
          <Card>
            <Flex vertical gap={16}>
              <Flex vertical gap={8}>
                <Flex justify="space-between">
                  <Space>
                    <Text weight={400}>
                      {t('stock_applications.issuance_based_on_inquire')}:
                    </Text>
                    <Text strong>#{data?.code}</Text>
                  </Space>
                  <Tag
                    color={
                      getStatus(
                        t,
                        data?.state ?? InternalInventoryDocumentState.NEW
                      ).color
                    }
                  >
                    {
                      getStatus(
                        t,
                        data?.state ?? InternalInventoryDocumentState.NEW
                      ).text
                    }
                  </Tag>
                </Flex>

                <Space>
                  <Text weight={400}>
                    {t('stock_applications.request_date')}:
                  </Text>
                  <Text strong>
                    {dayjs(data?.documentDate).format(
                      dayMonthYearHourMinutesFormatSeparator
                    )}
                  </Text>
                </Space>

                <Space>
                  <Text weight={400}>
                    {t('stock_applications.initiated_by')}:
                  </Text>
                  <Text strong>
                    {data?.initializedBy?.firstName}{' '}
                    {data?.initializedBy?.lastName} - {data?.role?.name}
                  </Text>
                </Space>
              </Flex>
            </Flex>
          </Card>

          <Card>
            <Flex vertical gap={16}>
              <LocalAdminConsumableTable
                isEdit={isEdit}
                data={data}
                form={form}
                isSubmitted={false}
              />
            </Flex>
          </Card>

          <Card>
            <Flex vertical gap={16}>
              <Card title={t('stock_applications.uploaded_documents')}>
                <Form.Item name="docs" className="full-width">
                  <Upload
                    defaultFileList={
                      data?.mediaFiles?.map((file) => ({
                        ...file,
                        uid: file.id,
                        name: file.name
                      })) as UploadFile[]
                    }
                    onChange={onFileChange}
                    customRequest={() => null}
                    itemRender={(_, file: FileDocument, __, { remove }) => (
                      <CustomItemRender
                        file={file}
                        onRemove={remove}
                        onDownload={() =>
                          onDownloadFile(t, file?.id, file?.name)
                        }
                      />
                    )}
                  />
                </Form.Item>
              </Card>
              <Card title={t('common.comment')}>
                <Flex vertical gap={16}>
                  {data?.comment && (
                    <Flex vertical gap={8}>
                      <Text size="lg">
                        {t('stock_applications.beneficiary')}
                      </Text>
                      <div className={styles.commentBeneficiary}>
                        {data?.comment}
                      </div>
                      <Text size="lg" color={palette.colorTextTertiary}>
                        {data?.commentBy?.firstName} {data?.commentBy?.lastName}{' '}
                        - {data?.role?.name}
                      </Text>
                    </Flex>
                  )}

                  <Flex vertical gap={8}>
                    {t('counterparts.provider')}
                    <Form.Item name="comment">
                      <Input.TextArea
                        rows={5}
                        placeholder={t('common.leave_comment')}
                      />
                    </Form.Item>
                  </Flex>
                </Flex>
              </Card>
            </Flex>
          </Card>
        </Flex>
      </Flex>

      <ConfirmModal
        open={isReject}
        title={`${t('stock_administration.reject')}?`}
        onCancel={() => setIsReject(false)}
        onOk={onReject}
        okButtonProps={{ danger: true }}
      >
        <Card>
          <Form.Item name="commentReject">
            <Input.TextArea rows={5} placeholder={t('common.leave_comment')} />
          </Form.Item>
        </Card>
      </ConfirmModal>
    </>
  )
}
